/**
 * The dismissible "Get the app" banner Q227 puts above the join dialog's
 * content. The web app is the only front door for a shared invitation, and
 * this is the affordance that offers the native app instead.
 * It offers a store only when the global admin has entered a link for that
 * platform, read from POST /site/v1/public/app-availability/get and passed in by
 * the caller (ISSUE-095). Until then it renders nothing at all.
 *
 * Renders nothing on a desktop user agent, and never swaps its action label
 * to "Open": no deep-link detection mechanism exists, and a browser cannot
 * reliably tell whether the native app is installed.
 */
import { Button, cn } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  storePlatformFromUserAgent,
  storeUrlFor,
  type typeStoreAvailability,
} from "../../shell/appStoreLinks";

export type typeGetTheAppBannerProps = {
  /** Pass navigator.userAgent. Injected so the component stays testable. */
  userAgent: string;
  /**
   * The `data` object of POST /site/v1/public/app-availability/get, or null
   * while the read has not resolved. Null, and a platform whose link is
   * unset, both mean this banner does not render.
   */
  availability: typeStoreAvailability | null;
  onDismiss: () => void;
};

export const GetTheAppBanner = (props: typeGetTheAppBannerProps): ReactNode => {
  const platform = storePlatformFromUserAgent(props.userAgent);
  const storeUrl = storeUrlFor(platform, props.availability);
  if (storeUrl === null) return null;

  return (
    <div
      data-testid="join-app-banner"
      className={cn(
        "flex items-center gap-3 rounded-md border border-line bg-surface p-3",
      )}
    >
      <div
        aria-hidden="true"
        className="grid h-8 w-8 shrink-0 place-items-center rounded-sm bg-primary text-body-strong text-ink-inverse"
      >
        S
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <p className="text-body-strong text-ink">Get the Sportsmet app</p>
        <p className="text-caption text-ink-secondary">
          Faster picks and alerts on your phone.
        </p>
      </div>
      <Button asChild variant="secondary" size="sm">
        <a href={storeUrl} rel="noopener noreferrer">
          Get the app
        </a>
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Dismiss"
        data-testid="join-app-banner-dismiss"
        onClick={props.onDismiss}
      >
        <svg
          className="h-icon-md w-icon-md"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="m5 5 10 10M15 5 5 15"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </Button>
    </div>
  );
};
